import type {
  ExecutedFailure as BaseExecutedFailure,
  LocalAudioTrack as BaseLocalAudioTrack,
  LocalVideoTrack as BaseLocalVideoTrack,
  VideoClient as BaseVideoClient,
} from '@zoom/videosdk';

import { BackgroundType, LocalMediaStatus, LocalVideoContextSetter } from '../core/types';

/**
 * Accurate types for the {@link BaseVideoClient}. Use
 * this instead of the type from the Zoom SDK.
 */
export type VideoClient = Omit<typeof BaseVideoClient, 'getCurrentUserInfo'> & {
  getCurrentUserInfo(
    ...args: Parameters<typeof BaseVideoClient.getCurrentUserInfo>
  ): ReturnType<typeof BaseVideoClient.getCurrentUserInfo> | undefined;
};

/**
 * Accurate types for the {@link BaseLocalVideoTrack}. Use
 * this instead of the type from the Zoom SDK.
 */
export interface LocalVideoTrack extends BaseLocalVideoTrack {
  isVideoStarted?: boolean;
}

/**
 * Accurate types for the {@link BaseLocalAudioTrack}. Use
 * this instead of the type from the Zoom SDK.
 */
export interface LocalAudioTrack extends BaseLocalAudioTrack {
  isMicUnmuted?: boolean;
  isAudioStarted?: boolean;
  audioContext?: AudioContext;
  audioStream?: MediaStream;
}

type BaseStartVideoArgs = {
  setLocalVideoContext: LocalVideoContextSetter;
  successStatus: LocalMediaStatus;
};

type BlurBackgroundArgs = {
  virtualBackgroundType: BackgroundType.BLUR;
  virtualBackgroundValue: { imageUrl: 'blur' };
};

type ImageBackgroundArgs = {
  virtualBackgroundType: BackgroundType.IMAGE;
  virtualBackgroundValue: {
    imageUrl: string;
  };
};

type NoBackgroundArgs = {
  virtualBackgroundType?: BackgroundType.NONE;
  virtualBackgroundValue?: undefined;
};

export type VirtualBackgroundArgs = BlurBackgroundArgs | ImageBackgroundArgs | NoBackgroundArgs;

export type StartVideoArgs = BaseStartVideoArgs & VirtualBackgroundArgs;

/**
 * https://developers.zoom.us/docs/video-sdk/web/error-codes
 */
export const enum ZoomErrorCode {
  CMD_CHANNEL_NOT_READY = 7104,
  /**
   * e.g. when failing to join a Zoom session due to network issues
   */
  CONNECTION_ERROR = 200,
  INSUFFICIENT_PRIVILEGES = 5007,
  OPERATION_TIMEOUT = 1,
  AUDIO_CAPTURE_FAILED = 6010,
}

export interface ExecutedFailure extends BaseExecutedFailure {
  subCode?: number;
}

/**
 * `subCode` values that Zoom attaches to an `AUDIO_CAPTURE_FAILED` rejection.
 * https://developers.zoom.us/docs/video-sdk/web/error-codes
 */
export const enum AudioCaptureFailedSubCode {
  PERMISSION_DENIED_BY_USER = 1,
  PERMISSION_DENIED_BY_SYSTEM = 2,
  DEVICE_IN_USE = 3,
  NO_DEVICE_FOUND = 4,
  UNKNOWN = 5,
  CONSTRAINT_VIOLATION = 6,
  PERMISSION_DISMISSED_BY_USER = 7,
}

export const AUDIO_CAPTURE_FAILED_SUBCODE_MESSAGE: Record<AudioCaptureFailedSubCode, string> = {
  [AudioCaptureFailedSubCode.PERMISSION_DENIED_BY_USER]: 'Permission denied by user',
  [AudioCaptureFailedSubCode.PERMISSION_DENIED_BY_SYSTEM]: 'Permission denied by system',
  [AudioCaptureFailedSubCode.DEVICE_IN_USE]: 'Device in use',
  [AudioCaptureFailedSubCode.NO_DEVICE_FOUND]: 'No device found',
  [AudioCaptureFailedSubCode.UNKNOWN]: 'Unknown reason',
  [AudioCaptureFailedSubCode.CONSTRAINT_VIOLATION]: 'Constraint violation',
  [AudioCaptureFailedSubCode.PERMISSION_DISMISSED_BY_USER]: 'Permission dismissed by user',
};
